/**
 * Single source of truth for CHOMP published pricing.
 */

/** Published range: exam may be $0 when proceeding; standalone exam fee applies otherwise */
export const SITE_PRICE_RANGE = '$0–$1,595+';

/** Fee when the client does not proceed with CHOMP-recommended dental treatment */
export const EXAM_STANDALONE_FEE = 50;

/** Lowest advertised cleaning (cat) — for CTAs like “From $495” */
export const CLEANING_START_CAT = 495;
export const CLEANING_START_DOG = 595;

/** Core visit (exam waived + blood + cleaning), before add-ons */
export const CORE_VISIT_MIN_CAT = 150 + CLEANING_START_CAT;
export const CORE_VISIT_MIN_DOG = 150 + CLEANING_START_DOG;

/** Two core visits per year for a dog (upper bound for “twice per year” marketing line) */
export const CORE_VISIT_TWICE_DOG = CORE_VISIT_MIN_DOG * 2;

/** Typical pay-as-you-go floor once common add-ons apply (marketing shorthand) */
export const ESTIMATED_TYPICAL_VISIT_MIN = 745;

/** Advanced / complex case starting point (service tier + schema) */
export const ADVANCED_CARE_FROM = 1595;

/** Prepaid second cleaning discount within 12 months of the first */
export const SECOND_CLEANING_PREPAY_SAVINGS = 150;

export const CHERRY_APPLY_URL = 'https://pay.withcherry.com/chomp-pet-dental';

export const alacartePricing = {
  doctorExam: {
    label: 'Doctor exam',
    displayPrice: 'Free',
    finePrint:
      'Complimentary when you proceed with CHOMP-recommended dental care. Without proceeding, the exam is $50.',
  },
  bloodWork: {
    label: 'Blood work',
    detail: 'CBC/Chemistry 10 panel',
    price: 150,
  },
  dentalCleaning: {
    label: 'Dental cleaning',
    includes: [
      'Anesthesia/sedation',
      'Dental X-rays (panoramic/intraoral)',
      'Scaling, charting, and polishing',
    ],
    cat: 495,
    dog: 595,
  },
  extractions: [
    { label: '1 root', price: 100 },
    { label: '2 root', price: 150 },
    { label: '3 root / canine', price: 200 },
  ],
  periovive: {
    label: 'Periovive',
    detail: 'Root planing & hyaluronic acid treatment',
    price: 75,
  },
  postOpMedications: {
    label: 'Post-op medications',
    detail: 'If required',
    min: 15,
    max: 30,
  },
} as const;

export type PricingRow = { service: string; value: string };

/** Itemized published rates shown as “Totally transparent pricing” */
export const transparentPricingSummary = {
  id: 'transparent-pricing' as const,
  name: 'Totally transparent pricing',
  subtitle:
    'Sample itemized costs for a pet that has a few extractions and requires some maintenance work and medications',
  rows: [
    { service: 'Doctor exam', value: 'Free*' },
    { service: 'Blood work', value: '$150' },
    { service: 'Dental cleaning', value: '$495 cat / $595 dog' },
    { service: 'Extractions', value: '$100–$200' },
    { service: 'Periovive', value: '$75' },
    { service: 'Post-op medications', value: '$15–$30 if required' },
  ] satisfies PricingRow[],
  totalLabel: 'Core subtotal (exam free)',
  total: `$${CORE_VISIT_MIN_CAT}+ cat / $${CORE_VISIT_MIN_DOG}+ dog`,
  twiceYearlyLabel: 'Twice per year',
  twiceYearlyValue: `Up to $${CORE_VISIT_TWICE_DOG.toLocaleString('en-US')}+`,
  footnote:
    '*Exam is complimentary when you move forward with recommended dental treatment; otherwise $50.',
} as const;

/** Prepaid second-cleaning offer for proactive pet parents */
export const secondCleaningOffer = {
  id: 'second-cleaning' as const,
  eyebrow: 'Stay ahead of dental disease',
  title: 'For proactive pet parents',
  body:
    'Save $150 on your pet’s second dental cleaning when you prepay and schedule it within 12 months of the first.',
  highlight: `Save $${SECOND_CLEANING_PREPAY_SAVINGS}`,
  highlightDetail: 'on the second cleaning',
} as const;

/** @deprecated Use transparentPricingSummary — kept as alias for any lingering imports */
export const payAsYouGoComparison = transparentPricingSummary;
